import React, { useMemo, useState } from "react";
import {
  Bike,
  ChefHat,
  Flame,
  Loader2,
  Timer,
  Truck,
} from "lucide-react";
import Modal from "../../../components/Modal";
import PedidoOriginalModal from "../../../components/PedidoOriginalModal";
import { OrderTypeBadge } from "../../../components/OrderTypeBadge";
import { useInspecaoPedidoTempo } from "../../../hooks/useInspecaoPedido";
import type { OperacaoDiaPedidoRow } from "../../../services/painelService";
import type {
  DescricaoItemPedido,
  InspecaoPedidoTempoResponse,
} from "../../../services/inspecaoService";
import { BrazilDate } from "../../../utils/BrazilDate";
import { PainelPedidoOriginalButton } from "../../Painel/components/PainelPedidoOriginalButton";
import { PainelCardActionRow } from "../../Painel/components/PainelCardActionRow";
import { resolveSalaoProducaoLabels } from "../../Painel/utils/origemPainel";
import {
  formatChegouHorario,
  formatParado,
  hhmmssToSegundos,
  segundosToHHMMSS,
} from "../utils/formatTempo";

interface SequenciaPedidoDetalhesModalProps {
  row: OperacaoDiaPedidoRow | null;
  lojaId: number | undefined;
  isOpen: boolean;
  onClose: () => void;
  podePriorizar: boolean;
  isPrioritario: boolean;
  onPriorizar: () => void;
  onRemoverPrioridade: () => void;
  isPriorizando: boolean;
  isRemovendo: boolean;
}

function formatDescricaoItem(item: DescricaoItemPedido): string[] {
  const lines: string[] = [];
  const name = item.name || item.item_original_name || "Item";
  lines.push(name);
  if (item.borda?.trim()) lines.push(`Borda: ${item.borda}`);
  if (item.sabores?.length) lines.push(`Sabores: ${item.sabores.join(", ")}`);
  if (item.observacao && String(item.observacao).trim()) {
    lines.push(`Obs: ${item.observacao}`);
  }
  return lines;
}

function etapaAtualTempoKey(
  etapa: OperacaoDiaPedidoRow["etapa"]
): keyof InspecaoPedidoTempoResponse["tempos"] | null {
  switch (etapa) {
    case "aguardando_pizzaiolo":
    case "aguardando_borda":
      return "aguardandoMontagem";
    case "em_montagem":
      return "montagem";
    case "no_forno":
      return "forno";
    case "aguardando_entrega":
      return "aguardandoEntrega";
    case "em_entrega":
      return "entrega";
    default:
      return null;
  }
}

function aplicarTempoEtapaAtual(
  tempos: InspecaoPedidoTempoResponse["tempos"],
  row: OperacaoDiaPedidoRow
): InspecaoPedidoTempoResponse["tempos"] {
  const key = etapaAtualTempoKey(row.etapa);
  if (!key || row.paradoSegundos == null || row.paradoSegundos <= 0) {
    return tempos;
  }
  const atual = hhmmssToSegundos(tempos[key]);
  if (atual > 0) return tempos;
  return {
    ...tempos,
    [key]: segundosToHHMMSS(row.paradoSegundos),
  };
}

const TEMPO_CARDS = [
  { key: "aguardandoMontagem" as const, label: "Aguardando montagem", icon: ChefHat },
  { key: "montagem" as const, label: "Montagem", icon: ChefHat },
  { key: "forno" as const, label: "Forno", icon: Flame },
  { key: "aguardandoEntrega" as const, label: "Aguardando entrega", icon: Truck },
  { key: "entrega" as const, label: "Entrega", icon: Bike },
  { key: "totalEtapas" as const, label: "Total das etapas", icon: Timer, highlight: true },
];

export const SequenciaPedidoDetalhesModal: React.FC<
  SequenciaPedidoDetalhesModalProps
> = ({
  row,
  lojaId,
  isOpen,
  onClose,
  podePriorizar,
  isPrioritario,
  onPriorizar,
  onRemoverPrioridade,
  isPriorizando,
  isRemovendo,
}) => {
  const [pedidoOriginalOrderId, setPedidoOriginalOrderId] = useState<
    number | null
  >(null);
  const hoje = BrazilDate.todayISO();

  const { data: temposData, isLoading, error } = useInspecaoPedidoTempo(
    isOpen && row
      ? {
          displayId: row.displayId,
          lojaId,
          dataInicio: hoje,
          dataFim: hoje,
        }
      : undefined
  );

  const pizzaDetalhes = useMemo(() => {
    if (!row || !temposData?.length) return [];
    return temposData.map((pizza) => ({
      ...pizza,
      tempos: aplicarTempoEtapaAtual(pizza.tempos, row),
    }));
  }, [row, temposData]);

  if (!isOpen || !row) return null;

  const emAndamento =
    row.etapa !== "finalizado" && row.etapa !== "cancelado";
  const salaoLabels =
    row.canalOrigem === "salao"
      ? resolveSalaoProducaoLabels({
          mesaId: row.salaoMesaId,
          comandaId: row.salaoComandaId,
          identificador: row.salaoIdentificador,
          displayId: row.displayId,
        })
      : null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Pedido #${row.displayId}${
          salaoLabels?.mesa || salaoLabels?.comandaLabel
            ? ` · ${[salaoLabels.mesa, salaoLabels.comandaLabel]
                .filter(Boolean)
                .join(" · ")}`
            : ""
        }`}
        panelClassName="max-w-2xl"
        bodyClassName="max-h-[75vh] overflow-y-auto p-4 md:p-6"
        compactHeader
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-xs text-neutral-500">
              {row.canalOrigem === "salao"
                ? `FoodFly · pedido ${row.orderId}`
                : `ID integração ${row.orderId}`}
            </p>
            {row.canalOrigem === "salao" &&
            (salaoLabels?.mesa || salaoLabels?.comandaLabel) ? (
              <p className="text-sm font-semibold text-neutral-800">
                {[salaoLabels.mesa, salaoLabels.comandaLabel]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : null}
            {row.canalOrigem === "salao" ? (
              salaoLabels?.cliente ? (
                <p className="text-sm text-neutral-600">{salaoLabels.cliente}</p>
              ) : null
            ) : (
              row.customerName?.trim() && (
                <p className="text-sm font-medium text-neutral-800">
                  {row.customerName.trim()}
                </p>
              )
            )}
          </div>
          {row.canalOrigem === "salao" ? (
            <span className="rounded-full bg-neutral-800 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-white">
              Salão
            </span>
          ) : (
            <OrderTypeBadge orderType={row.orderType} />
          )}
        </div>

        <div className="mb-4 rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-neutral-700">
            <span>
              Na fila:{" "}
              <strong className="tabular-nums">
                {formatChegouHorario(row.chegouEm)}
              </strong>
            </span>
            <span>
              Etapa: <strong>{row.etapaLabel}</strong>
            </span>
            {emAndamento && row.paradoSegundos != null && (
              <span>
                Parado há:{" "}
                <strong className="tabular-nums">
                  {formatParado(row.paradoSegundos)}
                </strong>
              </span>
            )}
          </div>
          {row.motivoParado && (
            <p className="mt-2 text-xs text-neutral-600">{row.motivoParado}</p>
          )}
          {row.montadores?.trim() && (
            <p className="mt-1 text-xs text-neutral-600">
              Montador: {row.montadores.trim()}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={28} className="animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {row.resumoItens ? (
              <>
                <p className="mb-2 font-medium">Itens</p>
                <p>{row.resumoItens}</p>
                <p className="mt-2 text-xs text-amber-800">
                  Rastreio detalhado por etapa ficará disponível quando o pedido
                  entrar na produção.
                </p>
              </>
            ) : (
              <>
                Rastreio detalhado indisponível para este pedido no momento.
                {(error as Error).message && (
                  <span className="mt-1 block text-xs opacity-80">
                    {(error as Error).message}
                  </span>
                )}
              </>
            )}
          </div>
        ) : (
          pizzaDetalhes.map((pizza, index) => (
            <div
              key={`${pizza.displayId}-${pizza.createdAt ?? index}`}
              className="mb-4 rounded-lg border border-neutral-200 bg-white p-3 last:mb-0"
            >
              {pizzaDetalhes.length > 1 && (
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Pizza {index + 1}
                  {pizza.createdAt && (
                    <span className="ml-2 font-normal normal-case text-neutral-400">
                      {BrazilDate.formatBR(
                        BrazilDate.fromISOString(pizza.createdAt),
                        true
                      )}
                    </span>
                  )}
                </p>
              )}

              {pizza.descricao &&
                Array.isArray(pizza.descricao) &&
                pizza.descricao.length > 0 && (
                  <ul className="mb-3 space-y-2 border-b border-neutral-100 pb-3">
                    {(pizza.descricao as DescricaoItemPedido[]).map(
                      (item, itemIndex) => (
                        <li key={itemIndex} className="text-sm text-neutral-800">
                          {formatDescricaoItem(item).map((line, lineIndex) => (
                            <div
                              key={lineIndex}
                              className={
                                lineIndex === 0
                                  ? "font-semibold"
                                  : "pl-2 text-xs text-neutral-600"
                              }
                            >
                              {line}
                            </div>
                          ))}
                        </li>
                      )
                    )}
                  </ul>
                )}

              <p className="mb-2 text-xs font-semibold text-neutral-700">
                Tempos por etapa
              </p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {TEMPO_CARDS.map(({ key, label, icon: Icon, highlight }) => (
                  <div
                    key={key}
                    className={`rounded-lg border p-2.5 ${
                      highlight
                        ? "border-primary bg-primary/5"
                        : "border-neutral-200 bg-neutral-50/80"
                    }`}
                  >
                    <div
                      className={`flex items-center gap-1.5 text-xs font-medium ${
                        highlight ? "text-primary" : "text-neutral-600"
                      }`}
                    >
                      <Icon size={14} />
                      {label}
                      {etapaAtualTempoKey(row.etapa) === key && emAndamento && (
                        <span className="rounded bg-blue-100 px-1 py-0.5 text-[10px] font-bold uppercase text-blue-800">
                          agora
                        </span>
                      )}
                    </div>
                    <div
                      className={`mt-1 text-base font-semibold tabular-nums ${
                        highlight ? "text-primary" : "text-neutral-900"
                      }`}
                    >
                      {pizza.tempos[key]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        <PainelCardActionRow>
          {row.orderId > 0 && (
            <PainelPedidoOriginalButton
              orderId={row.orderId}
              onOpen={setPedidoOriginalOrderId}
            />
          )}

          {podePriorizar && emAndamento && (
            isPrioritario ? (
              <button
                type="button"
                onClick={onRemoverPrioridade}
                disabled={isRemovendo}
                className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
              >
                {isRemovendo ? "Removendo…" : "Remover prioridade"}
              </button>
            ) : (
              <button
                type="button"
                onClick={onPriorizar}
                disabled={isPriorizando}
                className="rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {isPriorizando ? "Priorizando…" : "Priorizar montagem"}
              </button>
            )
          )}
        </PainelCardActionRow>
      </Modal>

      {pedidoOriginalOrderId != null && (
        <PedidoOriginalModal
          orderId={pedidoOriginalOrderId}
          lojaId={lojaId}
          isOpen
          onClose={() => setPedidoOriginalOrderId(null)}
          overlayZIndexClass="z-[10000]"
        />
      )}
    </>
  );
};
